import React, { useState, useRef, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { Snackbar, Alert } from '@mui/material';
import "./UploadShorts.css";
import { getAllVotesWithFilters } from "../../api/Vote";
import { uploadShorts } from "../../api/shortsVideo";
import Loader from "../Loader";
import { useNavigate } from "react-router-dom";
import { getUserId, getToken } from "../../api/index";
import { useSelector } from 'react-redux';
import SubscriptionPrompt from '../Shared/SubscriptionPrompt';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';

const UploadShorts = () => {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
    const [isNextClicked, setIsNextClicked] = useState(false);
    const [allVotes, setAllVotes] = useState([]);
    const fileInputRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedVoteId, setSelectedVoteId] = useState(null);
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [creatorFilter, setCreatorFilter] = useState('All'); // 'All', 'Admin', 'Community'
    const [sortFilter, setSortFilter] = useState(''); // '', 'Newest', 'Oldest', 'Popular'
    const [showCreatorDropdown, setShowCreatorDropdown] = useState(false);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });
    const navigate = useNavigate();

    const storedUserDetails = useSelector((state) => state.userDetails?.userDetails);
    const isSubscribed = (storedUserDetails && typeof storedUserDetails.isSubscribed !== 'undefined') ? storedUserDetails.isSubscribed : true;
    const isSubscriptionEnable = (storedUserDetails && typeof storedUserDetails.isSubscriptionEnable !== 'undefined') ? storedUserDetails.isSubscriptionEnable : false;
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const validateVideoDuration = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                const duration = video.duration; // Duration in seconds
                if (duration <= 60) { // 60 seconds = 1 minute
                    resolve(true);
                } else {
                    reject(false);
                }
            };

            video.onerror = () => {
                window.URL.revokeObjectURL(video.src);
                reject(false);
            };

            video.src = URL.createObjectURL(file);
        });
    };

    const compressVideo = (file, quality = 0.8, maxWidth = 720, maxHeight = 1280) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            video.onloadedmetadata = () => {
                // Calculate new dimensions while maintaining aspect ratio
                let { videoWidth, videoHeight } = video;
                const aspectRatio = videoWidth / videoHeight;

                if (videoWidth > maxWidth) {
                    videoWidth = maxWidth;
                    videoHeight = maxWidth / aspectRatio;
                }

                if (videoHeight > maxHeight) {
                    videoHeight = maxHeight;
                    videoWidth = maxHeight * aspectRatio;
                }

                canvas.width = videoWidth;
                canvas.height = videoHeight;

                // Set up MediaRecorder for video compression
                const stream = canvas.captureStream(30); // 30 FPS
                const mediaRecorder = new MediaRecorder(stream, {
                    mimeType: 'video/webm;codecs=vp9',
                    videoBitsPerSecond: 1000000 * quality // Adjust bitrate based on quality
                });

                const chunks = [];
                mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        chunks.push(e.data);
                    }
                };

                mediaRecorder.onstop = () => {
                    const compressedBlob = new Blob(chunks, { type: 'video/webm' });
                    const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, '.webm'), {
                        type: 'video/webm',
                        lastModified: Date.now()
                    });

                    window.URL.revokeObjectURL(video.src);
                    resolve(compressedFile);
                };

                mediaRecorder.onerror = (e) => {
                    window.URL.revokeObjectURL(video.src);
                    reject(e);
                };

                // Start recording
                mediaRecorder.start();

                // Draw video frames to canvas
                const drawFrame = () => {
                    if (video.ended || video.paused) {
                        mediaRecorder.stop();
                        return;
                    }

                    ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
                    requestAnimationFrame(drawFrame);
                };

                video.onplay = () => {
                    drawFrame();
                };

                // Start playing the video
                video.play().catch(reject);
            };

            video.onerror = () => {
                window.URL.revokeObjectURL(video.src);
                reject(new Error('Failed to load video for compression'));
            };

            video.src = URL.createObjectURL(file);
            video.muted = true; // Mute to allow autoplay
        });
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('video/')) {
            try {
                await validateVideoDuration(file);
                setSelectedVideo(file);
                setVideoPreviewUrl(URL.createObjectURL(file));
            } catch (error) {
                showSnackbar('Please upload video which is less than 1 minute', 'error');
                event.target.value = null;
            }
        } else {
            showSnackbar('Please select a valid video file', 'error');
            event.target.value = null;
        }
    };

    const handleSelectClick = () => {
        if (isSubscriptionEnable && !isSubscribed) {
            setSubscriptionDialogOpen(true);
            return;
        }
        fileInputRef.current.click();
    };

    const handleRemoveVideo = () => {
        setSelectedVideo(null);
        setVideoPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleAllVotes = async () => {
        const token = getToken();
        const userId = getUserId();

        // Build filter params - only selected filter is true
        const filterParams = {
            userId: userId,
            skip: 0,
            take: 100000,
            isLocation: false,
            isCreatedbyUser: creatorFilter === 'Community',
            isCreatedbySuperAdmin: creatorFilter === 'Admin',
            isOldest: sortFilter === 'Oldest' ? true : (sortFilter === 'Newest' ? false : false),
            mostPopular: sortFilter === 'Popular'
        };

        try {
            const response = await getAllVotesWithFilters(filterParams, token);
            console.log("response", response);
            setAllVotes(response);
        } catch (error) {
            console.log("error", error);
        }
    };

    useEffect(() => {
        if (selectedVideo) {
            handleAllVotes();
        }
    }, [selectedVideo, creatorFilter, sortFilter, handleAllVotes]);

    const handleUploadShorts = async () => {
        if (isSubscriptionEnable && !isSubscribed) {
            setSubscriptionDialogOpen(true);
            return;
        }
        if (loading) {
            return;
        }
        setLoading(true);
        setProgress(0);
        console.log("selectedVoteId", selectedVoteId);

        try {
            // Show compression progress
            showSnackbar('Processing video...', 'info');

            // Simulate compression progress
            for (let i = 0; i <= 50; i += 10) {
                setProgress(i);
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            // Compress the video before uploading
            const compressedVideo = await compressVideo(selectedVideo);
            console.log('Original size:', selectedVideo.size, 'Compressed size:', compressedVideo.size);
            console.log('Compression ratio:', (compressedVideo.size / selectedVideo.size * 100).toFixed(2) + '%');

            setProgress(60);

            // showSnackbar('Video compressed successfully! Uploading...', 'success');

            const token = getToken();
            const userId = getUserId();
            const credentials = {
                UserId: userId,
                VideoFile: compressedVideo, // Use compressed video instead of original
                VoteId: selectedVoteId,
                Description: description,
            }

            // Simulate upload progress
            for (let i = 60; i <= 90; i += 10) {
                setProgress(i);
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            const response = await uploadShorts(credentials, token);
            console.log("response", response);

            setProgress(100);
            await new Promise(resolve => setTimeout(resolve, 500));

            setIsNextClicked(true);
            showSnackbar('Video uploaded successfully!', 'success');
        } catch (error) {
            console.log("error", error);
            if (error.message && error.message.includes('compression')) {
                showSnackbar('Video compression failed. Uploading original video...', 'warning');
                // Fallback to original video if compression fails
                try {
                    setProgress(70);
                    const token = getToken();
                    const userId = getUserId();
                    const credentials = {
                        UserId: userId,
                        VideoFile: selectedVideo,
                        VoteId: selectedVoteId,
                        Description: description,
                    }

                    for (let i = 70; i <= 90; i += 10) {
                        setProgress(i);
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }

                    const response = await uploadShorts(credentials, token);
                    console.log("response", response);

                    setProgress(100);
                    await new Promise(resolve => setTimeout(resolve, 500));

                    setIsNextClicked(true);
                    showSnackbar('Video uploaded successfully!', 'success');
                } catch (fallbackError) {
                    console.log("fallback error", fallbackError);
                    showSnackbar('Failed to upload video. Please try again.', 'error');
                }
            } else {
                showSnackbar('Failed to upload video. Please try again.', 'error');
            }
        }
        setLoading(false);
        setProgress(0);
    }

    const handleGoToShortsVideo = () => {
        navigate("/shorts-videos");
    }
    return (
        <div className="uploadShortsContainer">
            <div className="uploadShortsTextContainer">
                <span className="uploadShortsText">Create New Shorts Video</span>
                {selectedVideo && !isNextClicked && (
                    <button
                        className="nextButton"
                        onClick={handleUploadShorts}
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        {loading ? <Loader containerSize="1rem" /> : 'Next'}
                    </button>
                )}
            </div>
            {!selectedVideo && !isNextClicked && (
                <div className="uploadActionContainer">
                    <span className="uploadActionContainerText">Upload media from your device</span>
                    <div className="uploadActionContainerBtn" onClick={handleSelectClick}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept="video/*"
                            style={{ display: 'none' }}
                        />
                        <button
                            className="uploadActionContainerBtn"

                        >
                            Select From Computer
                        </button>
                    </div>
                    <span className="uploadActionContainerNote"><b style={{ color: "#5AAF9A" }}>Note - </b> Video should be less than 1 minutes in length</span>
                </div>
            )}
            {selectedVideo && !isNextClicked && (
                <div className="videoPreviewContainer">
                    <div className="videoWrapper">
                        <div className="closeIconContainer" onClick={handleRemoveVideo}>
                            <CloseIcon className="closeIcon" sx={{ fontSize: { xs: '10px', sm: '12px', md: '15px', lg: '18px', xl: '20px' } }} />
                        </div>
                        <video
                            src={videoPreviewUrl}
                            className="videoPreview"
                            controls
                            autoPlay
                            muted
                            loop
                        />
                    </div>
                    <div className="videoDescriptionContainer">
                        <div className="videoInputContainer">
                            <textarea className="videoDescriptionTextarea" placeholder="Describe your video" onChange={e => setDescription(e.target.value)} />
                        </div>
                        <span className="videoAttachVoteText">Attach Vote</span>
                        <div className="videoVoteSelectContainer">
                            {/* Search and Filter Row */}
                            <div className="searchAndFilterRow">
                                <input
                                    type="text"
                                    className="videoVoteSearchBar"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <button
                                    className="filterToggleBtn"
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <TuneRoundedIcon sx={{ fontSize: 20 }} />
                                </button>
                            </div>

                            {/* Filter Dropdowns */}
                            {showFilters && (
                                <div className="filterDropdownsRow">
                                    {/* Creator Filter */}
                                    <div className="filterDropdownGroup">
                                        <span className="filterDropdownLabel">Creator</span>
                                        <div
                                            className="filterDropdownSelect"
                                            onClick={() => {
                                                setShowCreatorDropdown(!showCreatorDropdown);
                                                setShowSortDropdown(false);
                                            }}
                                        >
                                            <span className="filterDropdownValue">{creatorFilter}</span>
                                            {showCreatorDropdown ?
                                                <ExpandLessRoundedIcon sx={{ color: 'white', fontSize: 18 }} /> :
                                                <ExpandMoreRoundedIcon sx={{ color: 'white', fontSize: 18 }} />
                                            }
                                        </div>
                                        {showCreatorDropdown && (
                                            <div className="filterDropdownOptions">
                                                {['All', 'Admin', 'Community'].map(option => (
                                                    <div
                                                        key={option}
                                                        className={`filterDropdownOption ${creatorFilter === option ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCreatorFilter(option);
                                                            setShowCreatorDropdown(false);
                                                        }}
                                                    >
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Sort Filter */}
                                    <div className="filterDropdownGroup">
                                        <span className="filterDropdownLabel">Sort</span>
                                        <div
                                            className="filterDropdownSelect"
                                            onClick={() => {
                                                setShowSortDropdown(!showSortDropdown);
                                                setShowCreatorDropdown(false);
                                            }}
                                        >
                                            <span className="filterDropdownValue">{sortFilter || 'Default'}</span>
                                            {showSortDropdown ?
                                                <ExpandLessRoundedIcon sx={{ color: 'white', fontSize: 18 }} /> :
                                                <ExpandMoreRoundedIcon sx={{ color: 'white', fontSize: 18 }} />
                                            }
                                        </div>
                                        {showSortDropdown && (
                                            <div className="filterDropdownOptions">
                                                {['Default', 'Newest', 'Oldest', 'Popular'].map(option => (
                                                    <div
                                                        key={option}
                                                        className={`filterDropdownOption ${(option === 'Default' ? sortFilter === '' : sortFilter === option) ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSortFilter(option === 'Default' ? '' : option);
                                                            setShowSortDropdown(false);
                                                        }}
                                                    >
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {(allVotes.filter(vote => vote.title.toLowerCase().includes(searchTerm.toLowerCase()) || vote.description.toLowerCase().includes(searchTerm.toLowerCase()))).map((vote, idx, arr) => (
                                <div
                                    className={`videoVoteSelectItem${idx !== arr.length - 1 ? " with-divider" : ""}${selectedVoteId === vote.voteId ? " selected" : ""}`}
                                    key={vote.voteId || idx}
                                    onClick={() => setSelectedVoteId(vote.voteId)}
                                    style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
                                >
                                    <div className="videoVoteSelectItemTextContainer">
                                        <span className="videoVoteSelectItemText">{vote.title}</span>
                                        <span className="videoVoteSelectItemText2">{vote.description}</span>
                                    </div>
                                    {selectedVoteId === vote.voteId && (
                                        <span className="videoVoteSelectItemCloseIcon"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setSelectedVoteId(null);
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: { xs: '10px', sm: '12px', md: '15px', lg: '18px', xl: '20px' } }} />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {loading && (
                        <div className="videoUploadProgressContainer">
                            <div className="progressBarContainer">
                                <div className="progressBarLabel">Processing video...</div>
                                <div className="progressBarWrapper">
                                    <div className="progressBar">
                                        <div
                                            className="progressBarFill"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <div className="progressBarText">{progress}%</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {isNextClicked && (
                <div className="uploadShortsNextContainer">
                    <div className="PasswordChanges">
                        <div className="passwordChangedContainer">
                            <span className="signInText">Video Uploaded</span>
                            <span className="signInText2">Go to Shorts Video to see your video</span>
                            <img
                                src="/Assets/PasswordChanged/TickIcon.svg"
                                alt="no img found"
                                className="tickIcon"
                            />
                            <div className="backToLoginContainer">
                                <button className="btnContainer"
                                    onClick={handleGoToShortsVideo}
                                >
                                    <span className="btnText">
                                        Go to Shorts Video
                                    </span>
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <SubscriptionPrompt
                open={subscriptionDialogOpen}
                onClose={() => setSubscriptionDialogOpen(false)}
                message={"Please purchase a subscription from the Android or iOS app to upload shorts."}
            />
        </div>
    );
};

export default UploadShorts;